/**
 * DragSort — dragging plan entries between the day lists of the planner.
 *
 * Built on pointer events rather than HTML5 drag-and-drop, which does not exist
 * on touch: `dragstart` never fires on a phone, and the guest-app is a
 * mobile-first PWA where the phone IS the primary device. Pointer events are
 * the one input model that covers finger, pen and mouse with the same code.
 *
 * The gesture starts on a grip, never on the row itself. A row that begins
 * dragging when you touch it is a row you cannot scroll past — the list lives
 * on a scrolling page, and the browser cannot tell "I am moving this" from "I
 * am moving the page" until the finger has already travelled. Restricting the
 * gesture to a small explicit handle removes the ambiguity entirely, which is
 * also how the reference planner does it ("nutze das kleine Icon, um sie zu
 * verschieben"). The chevron and remove buttons stay as the keyboard- and
 * screen-reader-reachable path to the same three operations.
 *
 * While a drag runs, the dragged row is replaced in the flow by a placeholder
 * of the same height and a copy of it follows the pointer in a fixed-position
 * layer. Moving the real element instead would work, but every reflow of a
 * mid-flight row inside a scrolling list makes the page jump under the finger.
 *
 * Listeners go through the owning State, so the framework disposes them on
 * exit — except the ones on `window`, which are added per drag and removed
 * when it ends (see _end).
 */
export class DragSort
{
    /** Travel before a press counts as a drag, in pixels. */
    static THRESHOLD = 5;

    /** How close to an edge of the scroller auto-scrolling kicks in. */
    static SCROLL_EDGE = 72;

    /** Fastest auto-scroll, in pixels per frame. */
    static SCROLL_SPEED = 14;

    /**
     * @param {IF.State} state    Owns the tracked listeners.
     * @param {HTMLElement} root  Contains the drop lists.
     * @param {Object} options
     * @param {HTMLElement} options.scroller  The scrolling ancestor (the shell's
     *        page-content slot), for auto-scroll near the edges.
     * @param {Function} options.onCommit     Called with the new arrangement as
     *        [ { itemId, dayDate, sortOrder } ] once a drag lands somewhere new.
     *        Not called when the row is dropped back where it started.
     */
    static attach( state, root, { scroller = null, onCommit = null } = {} )
    {
        if ( !root )
        {
            return null;
        }
        return new DragSort( state, root, scroller, onCommit );
    }

    constructor( state, root, scroller, onCommit )
    {
        this.state = state;
        this.root = root;
        this.scroller = scroller;
        this.onCommit = onCommit;

        this.drag = null;
        this._frame = null;
        this._scrollBy = 0;

        // One delegated listener rather than one per grip: the planner
        // re-renders after every change, and re-binding dozens of rows each
        // time is both slower and easy to get wrong.
        this.state.addEventListener( this.root, "pointerdown", ( event ) => this._onDown( event ) );

        // Bound once and reused, so _end can take them off window again.
        this._onMove = ( event ) => this._move( event );
        this._onUp = ( event ) => this._up( event );
        this._onCancel = () => this._cancel();
    }

    /**
     * Abandon any drag in flight.
     *
     * The window listeners a drag installs are added per gesture, not through
     * the State, so leaving the screen with a finger still down would otherwise
     * leave them (and the floating copy) behind on a disposed screen.
     */
    destroy()
    {
        if ( this.drag )
        {
            this._cancel();
        }
    }

    /* --------------------------------- Start --------------------------------- */

    _onDown( event )
    {
        if ( this.drag || event.button > 0 )
        {
            return;
        }

        const grip = event.target.closest( "[data-grip]" );
        if ( !grip || !this.root.contains( grip ) )
        {
            return;
        }

        const element = grip.closest( "[data-item]" );
        const from = element && element.closest( "[data-drop]" );
        if ( !element || !from )
        {
            return;
        }

        // The grip carries `touch-action: none` (see screens.css), so the
        // browser has already agreed not to scroll from here — claiming the
        // gesture is safe.
        event.preventDefault();

        this.drag = {
            "pointerId" : event.pointerId,
            "element" : element,
            "grip" : grip,
            "fromList" : from,
            "fromIndex" : DragSort._indexOf( element ),
            "startX" : event.clientX,
            "startY" : event.clientY,
            "offsetX" : 0,
            "offsetY" : 0,
            "started" : false,
            "clone" : null,
            "placeholder" : null
        };

        // Captured so the drag survives the pointer leaving the grip — which it
        // does immediately, since the row moves out from under the finger.
        grip.setPointerCapture( event.pointerId );

        window.addEventListener( "pointermove", this._onMove, { passive : false } );
        window.addEventListener( "pointerup", this._onUp );
        window.addEventListener( "pointercancel", this._onCancel );
    }

    _begin( event )
    {
        const drag = this.drag;
        const rect = drag.element.getBoundingClientRect();

        // Where inside the row the finger is, so the copy sits under it rather
        // than snapping its top-left corner to the pointer.
        drag.offsetX = drag.startX - rect.left;
        drag.offsetY = drag.startY - rect.top;

        const placeholder = document.createElement( "div" );
        placeholder.className = "dragsort__placeholder";
        placeholder.style.height = rect.height + "px";

        const clone = drag.element.cloneNode( true );
        clone.classList.add( "dragsort__ghost" );
        clone.style.width = rect.width + "px";
        clone.removeAttribute( "data-item" );
        // A copy of a row full of buttons must not be reachable by tab or by a
        // screen reader — the real row is still the one that exists.
        clone.setAttribute( "aria-hidden", "true" );
        clone.querySelectorAll( "button" ).forEach( ( button ) => { button.tabIndex = -1; } );

        drag.element.after( placeholder );
        drag.element.classList.add( "dragsort__source" );
        // Inside the device frame, so a desktop keeps the row within the phone
        // mock instead of floating it over the whole browser window.
        ( this.root.closest( ".ga-root" ) || document.body ).appendChild( clone );

        drag.placeholder = placeholder;
        drag.clone = clone;
        drag.started = true;

        this.root.classList.add( "is-dragging" );
        this._position( event.clientX, event.clientY );
    }

    /* --------------------------------- Move ---------------------------------- */

    _move( event )
    {
        const drag = this.drag;
        if ( !drag || event.pointerId !== drag.pointerId )
        {
            return;
        }

        if ( !drag.started )
        {
            const travelled = Math.abs( event.clientY - drag.startY ) +
                              Math.abs( event.clientX - drag.startX );
            if ( travelled < DragSort.THRESHOLD )
            {
                return;
            }
            this._begin( event );
        }

        event.preventDefault();

        this._position( event.clientX, event.clientY );
        this._reposition( event.clientX, event.clientY );
        this._autoScroll( event.clientY );
    }

    _position( x, y )
    {
        const drag = this.drag;
        drag.clone.style.transform =
            "translate(" + ( x - drag.offsetX ) + "px," + ( y - drag.offsetY ) + "px)";
    }

    /**
     * Put the placeholder where the row would land.
     *
     * The list under the pointer is found with elementFromPoint on the LIST
     * rather than on a row, because the pointer spends much of a drag over the
     * gap between rows, over the placeholder itself, or over an empty day —
     * and an empty day is a perfectly good destination that has no rows to hit.
     */
    _reposition( x, y )
    {
        const drag = this.drag;

        // The floating copy is under the pointer by construction; ignoring it
        // is what lets the row beneath be found at all.
        drag.clone.style.pointerEvents = "none";
        const under = document.elementFromPoint( x, y );
        const list = under && under.closest( "[data-drop]" );

        if ( !list || !this.root.contains( list ) )
        {
            return;
        }

        const before = DragSort._insertBefore( list, y, drag.placeholder );
        if ( before )
        {
            list.insertBefore( drag.placeholder, before );
        }
        else
        {
            list.appendChild( drag.placeholder );
        }
    }

    /** The first row whose middle is below the pointer — insert ahead of it. */
    static _insertBefore( list, y, placeholder )
    {
        const rows = Array.from( list.querySelectorAll( "[data-item]" ) )
            .filter( ( row ) => !row.classList.contains( "dragsort__source" ) );

        for ( const row of rows )
        {
            const rect = row.getBoundingClientRect();
            if ( y < rect.top + rect.height / 2 )
            {
                return row;
            }
        }
        return null;
    }

    /**
     * Scroll the page while the pointer sits near its top or bottom edge.
     *
     * Without this a phone can only reorder within one screenful: the finger is
     * holding the row, so nothing is left to scroll the list with, and a day
     * four sections down simply cannot be reached.
     */
    _autoScroll( y )
    {
        if ( !this.scroller )
        {
            return;
        }

        const rect = this.scroller.getBoundingClientRect();
        const top = y - rect.top;
        const bottom = rect.bottom - y;

        if ( top < DragSort.SCROLL_EDGE )
        {
            this._scrollBy = -DragSort._speed( DragSort.SCROLL_EDGE - top );
        }
        else if ( bottom < DragSort.SCROLL_EDGE )
        {
            this._scrollBy = DragSort._speed( DragSort.SCROLL_EDGE - bottom );
        }
        else
        {
            this._scrollBy = 0;
        }

        if ( this._scrollBy !== 0 && this._frame === null )
        {
            this._frame = requestAnimationFrame( () => this._tick() );
        }
    }

    static _speed( depth )
    {
        return Math.ceil( Math.min( depth / DragSort.SCROLL_EDGE, 1 ) * DragSort.SCROLL_SPEED );
    }

    _tick()
    {
        this._frame = null;
        if ( !this.drag || this._scrollBy === 0 )
        {
            return;
        }

        this.scroller.scrollTop += this._scrollBy;
        this._frame = requestAnimationFrame( () => this._tick() );
    }

    /* ---------------------------------- End ---------------------------------- */

    _up( event )
    {
        const drag = this.drag;
        if ( !drag || event.pointerId !== drag.pointerId )
        {
            return;
        }

        if ( !drag.started )
        {
            // A tap on the grip that never became a drag. Nothing moved.
            this._end();
            return;
        }

        const list = drag.placeholder.parentElement;
        // The row takes the placeholder's slot — which is where the guest has
        // been watching it hover for the length of the drag.
        drag.placeholder.replaceWith( drag.element );

        const changed = list !== drag.fromList || DragSort._indexOf( drag.element ) !== drag.fromIndex;

        this._end();

        if ( changed && this.onCommit )
        {
            this.onCommit( this._layout() );
        }
    }

    _cancel()
    {
        const drag = this.drag;
        if ( drag && drag.started )
        {
            // Back where it came from — a cancelled gesture is not a move.
            const at = this.drag.fromList.children[ this.drag.fromIndex ] || null;
            this.drag.fromList.insertBefore( drag.element, at );
            drag.placeholder.remove();
        }
        this._end();
    }

    _end()
    {
        const drag = this.drag;

        window.removeEventListener( "pointermove", this._onMove );
        window.removeEventListener( "pointerup", this._onUp );
        window.removeEventListener( "pointercancel", this._onCancel );

        if ( this._frame !== null )
        {
            cancelAnimationFrame( this._frame );
            this._frame = null;
        }
        this._scrollBy = 0;

        if ( drag )
        {
            try
            {
                drag.grip.releasePointerCapture( drag.pointerId );
            }
            catch ( e )
            {
                // The pointer is already gone (the gesture ended off-screen);
                // there is nothing to release and nothing to report.
            }

            if ( drag.clone ) drag.clone.remove();
            if ( drag.placeholder ) drag.placeholder.remove();
            drag.element.classList.remove( "dragsort__source" );
        }

        this.root.classList.remove( "is-dragging" );
        this.drag = null;
    }

    /**
     * The whole board as the API takes it: every row, the day it now sits on
     * and its position within that day.
     *
     * The complete arrangement rather than the one row that moved, because a
     * drag renumbers two lists at once — see the PUT /plan/:planId/order
     * request class for why the result is what gets sent.
     */
    _layout()
    {
        const placements = [];

        this.root.querySelectorAll( "[data-drop]" ).forEach( ( list ) =>
        {
            const day = list.getAttribute( "data-drop" );
            const dayDate = day === "unsorted" ? null : day;

            let order = 0;
            list.querySelectorAll( "[data-item]" ).forEach( ( row ) =>
            {
                // The stay markers are the server's to place.
                if ( row.getAttribute( "data-fixed" ) === "1" )
                {
                    return;
                }
                placements.push( {
                    "itemId" : row.getAttribute( "data-item" ),
                    "dayDate" : dayDate,
                    "sortOrder" : order++
                } );
            } );
        } );

        return placements;
    }

    static _indexOf( element )
    {
        return Array.prototype.indexOf.call( element.parentElement.children, element );
    }
}
